import { useEffect } from 'react';
import { numberKeypressFilter } from 'helpers/numberKeypressFilter';

/**
 * Global delegated input behaviors of the project-board MainView
 * (view/main.js events hash — applied app-wide to ALL tabs; inventory §3.1 /
 * parity 27–34). Attached at document level while the board is mounted and
 * scoped to #ksdm-content (the Backbone view's el), so header inputs are NOT
 * affected.
 *
 * Covered here:
 *  - keyup ENTER on input/select → focus next non-readonly input (tabToNext).
 *  - dblclick input → select-all (non-readonly only).
 *  - keypress/keydown on contenteditable `.textarea` → maxlength typing guard.
 *  - keypress on `input[data-type=number], [data-formatter_type=decimal|integer]`
 *    → integer/decimal character filtering ('.' decimal-only & once; '-' only
 *    at pos 0 / when data-min < 0 with select-all).
 *  - keypress/paste `.press_english` → English-only filter.
 *  - keypress `.phone-number-input` → phone character filter.
 *
 * NOT ported (no 3a targets): the `input[data-type="number"]`
 * focus/keydown/keyup/blur spinner suite (arrow stepping, `.invalid` range
 * class, comma-reformat blur) — no Part A General Info field uses
 * data-type="number"; it ships with the sub-phase that introduces such fields.
 * `.text-clear` clicks are handled by the form components themselves (the
 * icons are React-rendered siblings).
 */
export function useGlobalInputBehaviors(): void {
  useEffect(() => {
    const inContent = (t: EventTarget | null): t is HTMLElement =>
      t instanceof HTMLElement && !!t.closest('#ksdm-content');

    // ---- keyup: ENTER tab-to-next (main.js tabToNext) ----
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const t = e.target;
      if (!inContent(t)) return;
      if (!(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return;

      let next: HTMLElement | null = null;
      // Own following siblings that are inputs first…
      let sib = t.nextElementSibling;
      while (sib) {
        if (sib instanceof HTMLInputElement) {
          next = sib;
          break;
        }
        sib = sib.nextElementSibling;
      }
      // …then inputs in following <li>s (first non-readonly).
      if (!next) {
        const li = t.closest('li');
        let l = li?.nextElementSibling ?? null;
        outer: while (l) {
          if (l.tagName === 'LI') {
            for (const inp of Array.from(l.querySelectorAll('input'))) {
              if (!inp.readOnly) {
                next = inp;
                break outer;
              }
            }
          }
          l = l.nextElementSibling;
        }
      }
      (next ?? t).focus();
    };

    // ---- dblclick: select-all (main.js selectField) ----
    const onDblClick = (e: MouseEvent) => {
      const t = e.target;
      if (!inContent(t)) return;
      if (t instanceof HTMLInputElement && !t.readOnly) t.select();
    };

    // ---- contenteditable .textarea maxlength guard (main.js checkTextarea) ----
    const NAV_KEYS = [
      'Backspace',
      'Delete',
      'End',
      'Home',
      'ArrowLeft',
      'ArrowUp',
      'ArrowRight',
      'ArrowDown',
    ];
    const checkTextarea = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const maxlength = Number(t.getAttribute('maxlength'));
      const text = t.textContent ?? '';
      if (text.length > maxlength && !NAV_KEYS.includes(e.key)) {
        e.preventDefault();
      }
    };

    const onKeyPress = (e: KeyboardEvent) => {
      const t = e.target;
      if (!inContent(t)) return;

      // contenteditable .textarea guard (keypress half)
      if (t.classList.contains('textarea') && t.isContentEditable) {
        checkTextarea(e);
        return;
      }

      if (t instanceof HTMLInputElement) {
        // .press_english: only [a-zA-Z0-9@._-]
        if (t.classList.contains('press_english')) {
          if (!/[a-zA-Z0-9@._-]/.test(e.key)) e.preventDefault();
          return;
        }
        // .phone-number-input: digits + '-+(). '
        if (t.classList.contains('phone-number-input')) {
          const allowed = '0123456789-+(). ';
          const control = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
          if (!allowed.includes(e.key) && !control.includes(e.key)) e.preventDefault();
          return;
        }
        // number char filtering
        const ft = t.dataset.formatter_type;
        if (t.dataset.type === 'number' || ft === 'decimal' || ft === 'integer') {
          numberKeypressFilter(e, t);
        }
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const t = e.target;
      if (!inContent(t)) return;
      // contenteditable .textarea guard (keydown half — catches Backspace etc.
      // which never fire keypress; preventDefault only applies over maxlength)
      if (t.classList.contains('textarea') && t.isContentEditable) {
        checkTextarea(e);
      }
    };

    // ---- .press_english paste blocker (main.js blockPaste) ----
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target;
      if (!inContent(t)) return;
      if (!(t instanceof HTMLInputElement) || !t.classList.contains('press_english')) return;
      const pasted = e.clipboardData?.getData('text') ?? '';
      const englishOnly = /^[A-Za-z0-9\s.,!?@#$%^&*()_\-+=:;"'<>/\\[\]{}|`~]*$/;
      if (!englishOnly.test(pasted)) e.preventDefault();
    };

    document.addEventListener('keyup', onKeyUp);
    document.addEventListener('dblclick', onDblClick);
    document.addEventListener('keypress', onKeyPress);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('dblclick', onDblClick);
      document.removeEventListener('keypress', onKeyPress);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('paste', onPaste);
    };
  }, []);
}
